/* eslint-disable no-plusplus */
import React, { useRef, useState, useEffect } from "react";
import { Stage, Layer, Circle, Text, Rect, Transformer, Group } from "react-konva";
import {
    Box,
    Stack,
    Button,
    Select,
    MenuItem,
    Typography,
    TextField,
    Paper,
    Chip,
    ButtonGroup,
    Menu,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    Tabs,
    Tab,
    Card,
    CardContent,
    AppBar,
    Toolbar,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
} from "@mui/material";
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import GridOnIcon from '@mui/icons-material/GridOn';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import ChairIcon from '@mui/icons-material/Chair';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

/* ================== CONFIG ================== */
const STAGE_WIDTH = 1200;
const STAGE_HEIGHT = 650;
const SEAT_RADIUS = 9;
const GRID_SIZE = 20;

/* ================== UTILS ================== */
const snap = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;
const generateId = () => Math.random().toString(36).substr(2, 9);

/* ================== COMPONENT ================== */
export default function SeatingPlanEditor() {
    const zones = [
        { id: "vip", label: "VIP", price: 30000, color: "#D4AF37" },
        { id: "standard", label: "Standard", price: 15000, color: "#4CAF50" },
        { id: "economy", label: "Economy", price: 8000, color: "#2196F3" },
        { id: "balcony", label: "Balcon", price: 12000, color: "#9C27B0" },
    ];

    const [seats, setSeats] = useState([]);
    const [selectedSeatIds, setSelectedSeatIds] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedZoneId, setSelectedZoneId] = useState("vip");
    const [scale, setScale] = useState(1);
    const [isGrouped, setIsGrouped] = useState({});
    const [showGrid, setShowGrid] = useState(true);
    
    // Selection rectangle
    const [selectionRect, setSelectionRect] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);

    const [rowLetter, setRowLetter] = useState("A");
    const [seatCount, setSeatCount] = useState(10);
    const [spacing, setSpacing] = useState(30);
    
    const [scene, setScene] = useState({
        x: 400,
        y: 80,
        width: 400,
        height: 80,
        label: "SCÈNE",
    });
    const [sceneSelected, setSceneSelected] = useState(false);

    // Context menu states
    const [contextMenu, setContextMenu] = useState(null);
    const [contextMenuType, setContextMenuType] = useState(null);
    const [contextMenuData, setContextMenuData] = useState(null);

    // UI States
    const [rightDrawerOpen, setRightDrawerOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(0);

    const sceneRef = useRef();
    const transformerRef = useRef();
    const rowGroupRef = useRef();
    const stageRef = useRef();

    const rows = [...new Set(seats.map(s => s.row))].filter(r => r !== "?");
    const getRowSeats = (rowLabel) => seats.filter(s => s.row === rowLabel);

    useEffect(() => {
        if (sceneSelected && transformerRef.current && sceneRef.current) {
            transformerRef.current.nodes([sceneRef.current]);
            transformerRef.current.getLayer().batchDraw();
        } else if (selectedRow && isGrouped[selectedRow] && transformerRef.current && rowGroupRef.current) {
            transformerRef.current.nodes([rowGroupRef.current]);
            transformerRef.current.getLayer().batchDraw();
        } else if (transformerRef.current) {
            transformerRef.current.nodes([]);
        }
    }, [sceneSelected, selectedRow, isGrouped]);

    // Context Menu Handlers
    const handleContextMenu = (e, type, data) => {
        e.evt.preventDefault();
        const stage = stageRef.current;
        const pointerPosition = stage.getPointerPosition();
        
        setContextMenu({
            mouseX: e.evt.clientX,
            mouseY: e.evt.clientY,
        });
        setContextMenuType(type);
        setContextMenuData(data);
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
        setContextMenuType(null);
        setContextMenuData(null);
    };

    // Actions
    const addSeat = () => {
        setSeats((prev) => [
            ...prev,
            {
                id: generateId(),
                x: 150,
                y: 150,
                row: "?",
                number: prev.length + 1,
                zoneId: selectedZoneId,
            },
        ]);
    };

    const addRow = () => {
        const newSeats = [];
        for (let i = 0; i < seatCount; i++) {
            newSeats.push({
                id: generateId(),
                x: snap(150 + i * spacing),
                y: snap(250),
                row: rowLetter,
                number: i + 1,
                zoneId: selectedZoneId,
            });
        }
        setSeats((prev) => [...prev, ...newSeats]);
        setIsGrouped(prev => ({ ...prev, [rowLetter]: true }));
        setRowLetter(String.fromCharCode(rowLetter.charCodeAt(0) + 1));
    };

    const updateSeat = (id, updates) => {
        setSeats((prev) =>
            prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
        );
    };

    const updateRow = (rowLabel, updates) => {
        setSeats((prev) =>
            prev.map((s) => {
                if (s.row === rowLabel) {
                    return { ...s, ...updates };
                }
                return s;
            })
        );
    };

    const deleteRow = (rowLabel) => {
        setSeats((prev) => prev.filter((s) => s.row !== rowLabel));
        setIsGrouped(prev => {
            const newGrouped = { ...prev };
            delete newGrouped[rowLabel];
            return newGrouped;
        });
        if (selectedRow === rowLabel) {
            setSelectedRow(null);
        }
    };

    const deleteSelectedSeats = () => {
        setSeats((prev) => prev.filter((s) => !selectedSeatIds.includes(s.id)));
        setSelectedSeatIds([]);
    };

    const duplicateRow = (rowLabel) => {
        const rowSeats = getRowSeats(rowLabel);
        const newRow = String.fromCharCode(rowLabel.charCodeAt(0) + 1);
        const newSeats = rowSeats.map(seat => ({
            ...seat,
            id: generateId(),
            row: newRow,
            y: seat.y + 40,
        }));
        setSeats((prev) => [...prev, ...newSeats]);
        setIsGrouped(prev => ({ ...prev, [newRow]: isGrouped[rowLabel] }));
        handleCloseContextMenu();
    };

    const groupRow = (rowLabel) => {
        setIsGrouped(prev => ({ ...prev, [rowLabel]: true }));
        setSelectedRow(rowLabel);
        setSelectedSeatIds([]);
        handleCloseContextMenu();
    };

    const ungroupRow = (rowLabel) => {
        setIsGrouped(prev => ({ ...prev, [rowLabel]: false }));
        setSelectedRow(null);
        handleCloseContextMenu();
    };

    const handleWheel = (e) => {
        e.evt.preventDefault();
        const scaleBy = 1.05;
        const newScale = e.evt.deltaY > 0 ? scale / scaleBy : scale * scaleBy;
        setScale(Math.min(Math.max(newScale, 0.5), 3));
    };

    const handleSave = () => {
        const planData = { 
            seats, 
            scene, 
            zones,
            groupedRows: isGrouped 
        };
        console.log("🔥 Seating plan to save:", planData);
        alert("Plan de salle sauvegardé!\n\nVoir la console pour les données.");
    };

    const handleStageClick = (e) => {
        if (e.target === e.target.getStage()) {
            setSelectedSeatIds([]);
            setSceneSelected(false);
            setSelectedRow(null);
        }
    };

    const handleMouseDown = (e) => {
        // Only start selection if clicking on stage background
        if (e.target !== e.target.getStage()) return;
        
        const stage = stageRef.current;
        const pos = stage.getPointerPosition();
        
        setIsSelecting(true);
        setSelectionRect({
            x: pos.x / scale,
            y: pos.y / scale,
            width: 0,
            height: 0,
        });
    };

    const handleMouseMove = (e) => {
        if (!isSelecting) return;
        
        const stage = stageRef.current;
        const pos = stage.getPointerPosition();
        
        setSelectionRect(prev => {
            if (!prev) return null;
            return {
                x: prev.x,
                y: prev.y,
                width: (pos.x / scale) - prev.x,
                height: (pos.y / scale) - prev.y,
            };
        });
    };

    const handleMouseUp = (e) => {
        if (!isSelecting) return;
        
        setIsSelecting(false);
        
        if (!selectionRect) return;
        
        // Calculate normalized rectangle
        const box = {
            x1: Math.min(selectionRect.x, selectionRect.x + selectionRect.width),
            y1: Math.min(selectionRect.y, selectionRect.y + selectionRect.height),
            x2: Math.max(selectionRect.x, selectionRect.x + selectionRect.width),
            y2: Math.max(selectionRect.y, selectionRect.y + selectionRect.height),
        };
        
        // Select all ungrouped seats within rectangle
        const selectedIds = seats
            .filter(seat => !isGrouped[seat.row])
            .filter(seat => {
                return seat.x >= box.x1 && 
                       seat.x <= box.x2 && 
                       seat.y >= box.y1 && 
                       seat.y <= box.y2;
            })
            .map(seat => seat.id);
        
        if (e.evt.shiftKey) {
            // Add to existing selection
            setSelectedSeatIds(prev => [...new Set([...prev, ...selectedIds])]);
        } else {
            // Replace selection
            setSelectedSeatIds(selectedIds);
        }
        
        setSelectionRect(null);
        setSelectedRow(null);
        setSceneSelected(false);
    };

    const handleRowDragEnd = (rowLabel, e) => {
        const deltaX = snap(e.target.x());
        const deltaY = snap(e.target.y());
        
        setSeats((prev) =>
            prev.map((s) => {
                if (s.row === rowLabel) {
                    return {
                        ...s,
                        x: s.x + deltaX,
                        y: s.y + deltaY,
                    };
                }
                return s;
            })
        );
        e.target.position({ x: 0, y: 0 });
    };

    const selectRow = (rowLabel) => {
        setSelectedRow(rowLabel);
        setSelectedSeatIds([]);
        setSceneSelected(false);
    };

    const toggleSeatSelection = (seatId, rowLabel) => {
        if (isGrouped[rowLabel]) return;
        setSelectedSeatIds(prev => {
            if (prev.includes(seatId)) {
                return prev.filter(id => id !== seatId);
            } 
                return [...prev, seatId];
            
        });
        setSelectedRow(null);
        setSceneSelected(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f7fa' }}>
            {/* AppBar */}
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#1e293b' }}>
                <Toolbar>
                    <TheaterComedyIcon sx={{ mr: 2 }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Éditeur de Plan de Salle
                    </Typography>
                    
                    <Tooltip title="Annuler">
                        <IconButton color="inherit" size="small">
                            <UndoIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Rétablir">
                        <IconButton color="inherit" size="small">
                            <RedoIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Divider orientation="vertical" flexItem sx={{ mx: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                    
                    <Tooltip title="Zoom -">
                        <IconButton color="inherit" onClick={() => setScale(s => Math.max(0.5, s / 1.2))}>
                            <ZoomOutIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ mx: 1, minWidth: 50, textAlign: 'center' }}>
                        {Math.round(scale * 100)}%
                    </Typography>
                    <Tooltip title="Zoom +">
                        <IconButton color="inherit" onClick={() => setScale(s => Math.min(3, s * 1.2))}>
                            <ZoomInIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Divider orientation="vertical" flexItem sx={{ mx: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                    
                    <Tooltip title="Grille">
                        <IconButton 
                            color={showGrid ? "primary" : "inherit"}
                            onClick={() => setShowGrid(!showGrid)}
                        >
                            <GridOnIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        sx={{ ml: 2, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                    >
                        Sauvegarder
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Main Canvas Area */}
                <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            display: 'inline-block',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}
                    >
                        <Stage
                            ref={stageRef}
                            width={STAGE_WIDTH}
                            height={STAGE_HEIGHT}
                            scaleX={scale}
                            scaleY={scale}
                            onWheel={handleWheel}
                            onClick={handleStageClick}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onContextMenu={(e) => {
                                e.evt.preventDefault();
                                handleContextMenu(e, 'canvas', null);
                            }}
                        >
                            <Layer>
                                {/* Grid */}
                                {showGrid && (
                                    <>
                                        {Array.from({ length: Math.ceil(STAGE_WIDTH / GRID_SIZE) }).map((_, i) => (
                                            <Rect
                                                key={`v-${i}`}
                                                x={i * GRID_SIZE}
                                                y={0}
                                                width={1}
                                                height={STAGE_HEIGHT}
                                                fill="#e5e7eb"
                                                listening={false}
                                            />
                                        ))}
                                        {Array.from({ length: Math.ceil(STAGE_HEIGHT / GRID_SIZE) }).map((_, i) => (
                                            <Rect
                                                key={`h-${i}`}
                                                x={0}
                                                y={i * GRID_SIZE}
                                                width={STAGE_WIDTH}
                                                height={1}
                                                fill="#e5e7eb"
                                                listening={false}
                                            />
                                        ))}
                                    </>
                                )}

                                {/* Selection Rectangle */}
                                {selectionRect && (
                                    <Rect
                                        x={selectionRect.x}
                                        y={selectionRect.y}
                                        width={selectionRect.width}
                                        height={selectionRect.height}
                                        fill="rgba(59, 130, 246, 0.2)"
                                        stroke="#3b82f6"
                                        strokeWidth={2 / scale}
                                        dash={[10 / scale, 5 / scale]}
                                        listening={false}
                                    />
                                )}
                            </Layer>

                            <Layer>
                                {/* Scene */}
                                {scene && (
                                    <>
                                        <Rect
                                            ref={sceneRef}
                                            x={scene.x}
                                            y={scene.y}
                                            width={scene.width}
                                            height={scene.height}
                                            fill="#1e293b"
                                            cornerRadius={12}
                                            shadowBlur={10}
                                            shadowColor="black"
                                            shadowOpacity={0.3}
                                            onClick={() => {
                                                setSceneSelected(true);
                                                setSelectedSeatIds([]);
                                                setSelectedRow(null);
                                            }}
                                            onContextMenu={(e) => handleContextMenu(e, 'scene', scene)}
                                            draggable
                                            onDragEnd={(e) =>
                                                setScene({
                                                    ...scene,
                                                    x: snap(e.target.x()),
                                                    y: snap(e.target.y()),
                                                })
                                            }
                                            onTransformEnd={(e) => {
                                                const node = sceneRef.current;
                                                const scaleX = node.scaleX();
                                                const scaleY = node.scaleY();
                                                node.scaleX(1);
                                                node.scaleY(1);
                                                setScene({
                                                    ...scene,
                                                    x: snap(node.x()),
                                                    y: snap(node.y()),
                                                    width: snap(Math.max(100, node.width() * scaleX)),
                                                    height: snap(Math.max(30, node.height() * scaleY)),
                                                });
                                            }}
                                        />
                                        <Text
                                            x={scene.x + scene.width / 2}
                                            y={scene.y + scene.height / 2}
                                            text={scene.label}
                                            fontSize={18}
                                            fill="#fff"
                                            fontStyle="bold"
                                            offsetX={scene.label.length * 4.5}
                                            offsetY={9}
                                            listening={false}
                                        />
                                    </>
                                )}

                                {/* Grouped Rows */}
                                {rows.filter(row => isGrouped[row]).map((row) => {
                                    const rowSeats = getRowSeats(row);
                                    const isRowSelected = selectedRow === row;

                                    return (
                                        <Group
                                            key={`row-${row}`}
                                            ref={isRowSelected ? rowGroupRef : null}
                                            draggable={isRowSelected}
                                            onClick={() => {
                                                if (!selectedSeatIds.length) {
                                                    selectRow(row);
                                                }
                                            }}
                                            onContextMenu={(e) => handleContextMenu(e, 'row', row)}
                                            onDragEnd={(e) => handleRowDragEnd(row, e)}
                                        >
                                            {rowSeats.map((seat) => {
                                                const zone = zones.find((z) => z.id === seat.zoneId);
                                                return (
                                                    <React.Fragment key={seat.id}>
                                                        <Circle
                                                            x={seat.x}
                                                            y={seat.y}
                                                            radius={SEAT_RADIUS}
                                                            fill={zone.color}
                                                            stroke={isRowSelected ? "#3b82f6" : "#64748b"}
                                                            strokeWidth={isRowSelected ? 3 : 1}
                                                            shadowBlur={isRowSelected ? 8 : 0}
                                                            shadowColor="#3b82f6"
                                                        />
                                                        <Text
                                                            x={seat.x}
                                                            y={seat.y}
                                                            text={`${seat.row}${seat.number}`}
                                                            fontSize={8}
                                                            fill="#000"
                                                            offsetX={6}
                                                            offsetY={4}
                                                            listening={false}
                                                        />
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Group>
                                    );
                                })}

                                {/* Ungrouped Seats */}
                                {seats.filter(s => !isGrouped[s.row]).map((seat) => {
                                    const zone = zones.find((z) => z.id === seat.zoneId);
                                    const isSelected = selectedSeatIds.includes(seat.id);

                                    return (
                                        <React.Fragment key={seat.id}>
                                            <Circle
                                                x={seat.x}
                                                y={seat.y}
                                                radius={SEAT_RADIUS}
                                                fill={zone.color}
                                                stroke={isSelected ? "#000" : "#64748b"}
                                                strokeWidth={isSelected ? 3 : 1}
                                                shadowBlur={isSelected ? 8 : 0}
                                                shadowColor="#000"
                                                draggable
                                                onClick={(e) => {
                                                    e.cancelBubble = true;
                                                    if (e.evt.shiftKey) {
                                                        toggleSeatSelection(seat.id, seat.row);
                                                    } else {
                                                        setSelectedSeatIds([seat.id]);
                                                        setSceneSelected(false);
                                                        setSelectedRow(null);
                                                    }
                                                }}
                                                onContextMenu={(e) => handleContextMenu(e, 'seat', seat)}
                                                onDragEnd={(e) =>
                                                    updateSeat(seat.id, {
                                                        x: snap(e.target.x()),
                                                        y: snap(e.target.y()),
                                                    })
                                                }
                                            />
                                            <Text
                                                x={seat.x}
                                                y={seat.y}
                                                text={`${seat.row}${seat.number}`}
                                                fontSize={8}
                                                fill="#000"
                                                offsetX={6}
                                                offsetY={4}
                                                listening={false}
                                            />
                                        </React.Fragment>
                                    );
                                })}

                                <Transformer
                                    ref={transformerRef}
                                    enabledAnchors={selectedRow ? [] : ["middle-left", "middle-right"]}
                                    boundBoxFunc={(oldBox, newBox) => {
                                        if (newBox.width < 100) return oldBox;
                                        return newBox;
                                    }}
                                />
                            </Layer>
                        </Stage>
                    </Paper>
                </Box>

                {/* Right Sidebar */}
                <Drawer
                    variant="persistent"
                    anchor="right"
                    open={rightDrawerOpen}
                    sx={{
                        width: 320,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: 320,
                            boxSizing: 'border-box',
                            position: 'relative',
                            borderLeft: '1px solid #e5e7eb',
                        },
                    }}
                >
                    <Box sx={{ overflow: 'auto', height: '100%' }}>
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                            <Typography variant="caption" color="text.secondary">
                                💡 Astuce : Cliquez-glissez sur le canvas pour sélectionner plusieurs sièges. 
                                Maintenez Shift pour ajouter à la sélection.
                            </Typography>
                        </Box>
                        
                        <Tabs 
                            value={activeTab} 
                            onChange={(e, v) => setActiveTab(v)}
                            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}
                        >
                            <Tab icon={<AddIcon />} label="Ajouter" />
                            <Tab icon={<LayersIcon />} label="Rangées" />
                            <Tab icon={<EditIcon />} label="Propriétés" />
                        </Tabs>

                        {/* Tab 0: Add Elements */}
                        {activeTab === 0 && (
                            <Box sx={{ p: 2 }}>
                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Zone tarifaire
                                        </Typography>
                                        <Select
                                            fullWidth
                                            value={selectedZoneId}
                                            onChange={(e) => setSelectedZoneId(e.target.value)}
                                            size="small"
                                        >
                                            {zones.map((z) => (
                                                <MenuItem key={z.id} value={z.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 20, height: 20, bgcolor: z.color, borderRadius: 1 }} />
                                                        {z.label} – {z.price.toLocaleString()} FCFA
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </CardContent>
                                </Card>

                                <Card sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Siège individuel
                                        </Typography>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<ChairIcon />}
                                            onClick={addSeat}
                                        >
                                            Ajouter un siège
                                        </Button>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Ajouter une rangée
                                        </Typography>
                                        <Stack spacing={2}>
                                            <TextField
                                                size="small"
                                                label="Lettre de rangée"
                                                value={rowLetter}
                                                onChange={(e) => setRowLetter(e.target.value.toUpperCase())}
                                            />
                                            <TextField
                                                size="small"
                                                label="Nombre de sièges"
                                                type="number"
                                                value={seatCount}
                                                onChange={(e) => setSeatCount(+e.target.value)}
                                            />
                                            <TextField
                                                size="small"
                                                label="Espacement (px)"
                                                type="number"
                                                value={spacing}
                                                onChange={(e) => setSpacing(+e.target.value)}
                                            />
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={addRow}
                                                startIcon={<AddIcon />}
                                            >
                                                Créer la rangée
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}

                        {/* Tab 1: Rows List */}
                        {activeTab === 1 && (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Rangées ({rows.length})
                                </Typography>
                                <List>
                                    {rows.map((row) => {
                                        const rowSeats = getRowSeats(row);
                                        const grouped = isGrouped[row];
                                        const isSelected = selectedRow === row;
                                        
                                        return (
                                            <Card key={row} sx={{ mb: 1, bgcolor: isSelected ? '#e3f2fd' : 'white' }}>
                                                <ListItem
                                                    secondaryAction={
                                                        <ButtonGroup size="small">
                                                            {grouped ? (
                                                                <IconButton 
                                                                    onClick={() => ungroupRow(row)}
                                                                    title="Dégrouper"
                                                                >
                                                                    <CallSplitIcon fontSize="small" />
                                                                </IconButton>
                                                            ) : (
                                                                <IconButton 
                                                                    onClick={() => groupRow(row)}
                                                                    title="Grouper"
                                                                >
                                                                    <GroupWorkIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            <IconButton
                                                                onClick={() => deleteRow(row)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </ButtonGroup>
                                                    }
                                                >
                                                    <ListItemButton onClick={() => selectRow(row)}>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="h6">Rangée {row}</Typography>
                                                                    {grouped && <GroupWorkIcon fontSize="small" color="primary" />}
                                                                </Box>
                                                            }
                                                            secondary={`${rowSeats.length} sièges`}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            </Card>
                                        );
                                    })}
                                </List>
                            </Box>
                        )}

                        {/* Tab 2: Properties */}
                        {activeTab === 2 && (
                            <Box sx={{ p: 2 }}>
                                {selectedSeatIds.length === 1 && (() => {
                                    const seat = seats.find(s => s.id === selectedSeatIds[0]);
                                    if (!seat) return null;
                                    
                                    return (
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Siège {seat.row}{seat.number}
                                                </Typography>
                                                <Stack spacing={2}>
                                                    <TextField
                                                        size="small"
                                                        label="Rangée"
                                                        value={seat.row}
                                                        onChange={(e) => updateSeat(seat.id, { row: e.target.value.toUpperCase() })}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        label="Numéro"
                                                        type="number"
                                                        value={seat.number}
                                                        onChange={(e) => updateSeat(seat.id, { number: +e.target.value })}
                                                    />
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={seat.zoneId}
                                                        onChange={(e) => updateSeat(seat.id, { zoneId: e.target.value })}
                                                    >
                                                        {zones.map((z) => (
                                                            <MenuItem key={z.id} value={z.id}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box sx={{ width: 20, height: 20, bgcolor: z.color, borderRadius: 1 }} />
                                                                    {z.label}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => {
                                                            deleteSelectedSeats();
                                                        }}
                                                    >
                                                        Supprimer
                                                    </Button>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })()}

                                {selectedSeatIds.length > 1 && (
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Sélection multiple
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {selectedSeatIds.length} sièges sélectionnés
                                            </Typography>
                                            <Stack spacing={2}>
                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    value=""
                                                    displayEmpty
                                                    onChange={(e) => {
                                                        selectedSeatIds.forEach(id => {
                                                            updateSeat(id, { zoneId: e.target.value });
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        Changer la zone
                                                    </MenuItem>
                                                    {zones.map((z) => (
                                                        <MenuItem key={z.id} value={z.id}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 20, height: 20, bgcolor: z.color, borderRadius: 1 }} />
                                                                {z.label}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={deleteSelectedSeats}
                                                >
                                                    Supprimer tout
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                )}

                                {selectedRow && (
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Rangée {selectedRow}
                                            </Typography>
                                            <Chip 
                                                label="Groupée" 
                                                color="primary" 
                                                size="small"
                                                icon={<GroupWorkIcon />}
                                                sx={{ mb: 2 }}
                                            />
                                            <Stack spacing={2}>
                                                <TextField
                                                    size="small"
                                                    label="Renommer"
                                                    value={selectedRow}
                                                    onChange={(e) => {
                                                        const newRow = e.target.value.toUpperCase();
                                                        updateRow(selectedRow, { row: newRow });
                                                        setIsGrouped(prev => {
                                                            const newGrouped = { ...prev };
                                                            newGrouped[newRow] = newGrouped[selectedRow];
                                                            delete newGrouped[selectedRow];
                                                            return newGrouped;
                                                        });
                                                        setSelectedRow(newRow);
                                                    }}
                                                />
                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    value={getRowSeats(selectedRow)[0]?.zoneId || "vip"}
                                                    onChange={(e) => updateRow(selectedRow, { zoneId: e.target.value })}
                                                >
                                                    {zones.map((z) => (
                                                        <MenuItem key={z.id} value={z.id}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 20, height: 20, bgcolor: z.color, borderRadius: 1 }} />
                                                                {z.label}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    startIcon={<CallSplitIcon />}
                                                    onClick={() => ungroupRow(selectedRow)}
                                                >
                                                    Dégrouper
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                )}

                                {!selectedSeatIds.length && !selectedRow && !sceneSelected && (
                                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                        <SettingsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                        <Typography>
                                            Sélectionnez un élément pour voir ses propriétés
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Drawer>
            </Box>

            {/* Context Menus */}
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                {contextMenuType === 'seat' && contextMenuData && (
                    <Box>
                        <MenuItem onClick={() => {
                            setSelectedSeatIds([contextMenuData.id]);
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Éditer</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            const newSeat = {
                                ...contextMenuData,
                                id: generateId(),
                                x: contextMenuData.x + 30,
                                y: contextMenuData.y + 30,
                            };
                            setSeats(prev => [...prev, newSeat]);
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Dupliquer</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => {
                            setSeats(prev => prev.filter(s => s.id !== contextMenuData.id));
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText>Supprimer</ListItemText>
                        </MenuItem>
                    </Box>
                )}

                {contextMenuType === 'row' && contextMenuData && (
                    <Box>
                        <MenuItem onClick={() => {
                            selectRow(contextMenuData);
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Éditer la rangée</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => duplicateRow(contextMenuData)}>
                            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Dupliquer la rangée</ListItemText>
                        </MenuItem>
                        {isGrouped[contextMenuData] ? (
                            <MenuItem onClick={() => ungroupRow(contextMenuData)}>
                                <ListItemIcon><CallSplitIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Dégrouper</ListItemText>
                            </MenuItem>
                        ) : (
                            <MenuItem onClick={() => groupRow(contextMenuData)}>
                                <ListItemIcon><GroupWorkIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Grouper</ListItemText>
                            </MenuItem>
                        )}
                        <Divider />
                        <MenuItem onClick={() => {
                            deleteRow(contextMenuData);
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText>Supprimer la rangée</ListItemText>
                        </MenuItem>
                    </Box>
                )}

                {contextMenuType === 'canvas' && (
                    <Box>
                        <MenuItem onClick={() => {
                            addSeat();
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Ajouter un siège</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            addRow();
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Ajouter une rangée</ListItemText>
                        </MenuItem>
                    </Box>
                )}

                {contextMenuType === 'scene' && (
                    <Box>
                        <MenuItem onClick={() => {
                            setSceneSelected(true);
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Éditer la scène</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            setScene(prev => ({ ...prev, label: prompt('Nouveau nom:', prev.label) || prev.label }));
                            handleCloseContextMenu();
                        }}>
                            <ListItemIcon><ColorLensIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Renommer</ListItemText>
                        </MenuItem>
                    </Box>
                )}
            </Menu>
        </Box>
    );
}